// Lightweight DOM UI: the "dive in" enter overlay and the gardening control panel.
// No framework — just createElement. Styles are injected once.

const STYLE = `
.ds-overlay {
  position: fixed; inset: 0; display: flex; flex-direction: column;
  align-items: center; justify-content: center; gap: 1.5rem;
  background: radial-gradient(circle at 50% 40%, #101010 0%, #000 70%);
  color: #f2f2f2; font-family: system-ui, -apple-system, sans-serif;
  z-index: 10; transition: opacity 1.2s ease; text-align: center; padding: 2rem;
}
.ds-overlay h1 { font-weight: 300; letter-spacing: 0.35em; font-size: clamp(1.5rem, 5vw, 2.8rem); margin: 0; text-transform: uppercase; }
.ds-overlay p { max-width: 32rem; line-height: 1.6; opacity: 0.6; font-weight: 300; margin: 0; }
.ds-enter {
  margin-top: 1rem; padding: 0.9rem 2.6rem; border: 1px solid rgba(255,255,255,0.55); border-radius: 999px;
  background: rgba(255,255,255,0.05); color: #fff; font-size: 1rem; letter-spacing: 0.2em;
  cursor: pointer; text-transform: uppercase; transition: all 0.3s ease;
}
.ds-enter:hover { background: rgba(255,255,255,0.16); box-shadow: 0 0 30px rgba(255,255,255,0.22); }
.ds-enter:disabled { opacity: 0.4; cursor: wait; }
.ds-hint { font-size: 0.75rem; opacity: 0.38; letter-spacing: 0.15em; }
.ds-panel {
  position: fixed; left: 1rem; bottom: 1rem; z-index: 5; display: flex; flex-direction: column;
  gap: 0.55rem; padding: 0.9rem 1.1rem; border-radius: 12px;
  background: rgba(0,0,0,0.66);
  -webkit-backdrop-filter: blur(8px); backdrop-filter: blur(8px);
  border: 1px solid rgba(255,255,255,0.2);
  color: #ededed; font-family: system-ui, sans-serif; font-size: 0.72rem; letter-spacing: 0.08em;
  width: 220px; transition: opacity 0.4s ease; user-select: none;
  /* Scroll instead of letting the (now taller) control stack push rows off a short viewport. */
  max-height: calc(100vh - 2rem); overflow-y: auto;
}
.ds-panel :focus-visible { outline: 1px solid #fff; outline-offset: 2px; }
.ds-panel.hidden { opacity: 0; pointer-events: none; }
.ds-panel-head { display: flex; align-items: center; gap: 0.5rem; }
.ds-panel-title { flex: 1; text-transform: uppercase; letter-spacing: 0.25em; font-size: 0.6rem; opacity: 0.55; }
.ds-min {
  width: 26px; height: 26px; border-radius: 5px; border: 1px solid rgba(255,255,255,0.28);
  background: transparent; color: #e8e8e8; cursor: pointer; line-height: 1; font-size: 0.9rem;
}
.ds-min:hover { background: #f2f2f2; color: #000; border-color: #fff; }
.ds-restore {
  display: none; width: 44px; height: 44px; border-radius: 9px; border: 1px solid rgba(255,255,255,0.3);
  background: rgba(0,0,0,0.55); color: #e8e8e8; cursor: pointer; font-size: 1.1rem;
}
.ds-restore:hover { background: #f2f2f2; color: #000; border-color: #fff; }
.ds-panel.minimized { width: auto; gap: 0; padding: 0.4rem; }
.ds-panel.minimized > :not(.ds-restore) { display: none; }
.ds-panel.minimized > .ds-restore { display: block; }
.ds-row { display: flex; align-items: center; justify-content: space-between; gap: 0.6rem; }
.ds-row label { opacity: 0.7; text-transform: uppercase; font-size: 0.64rem; }
.ds-row input[type=range] { flex: 1; accent-color: #fff; height: 2px; }
.ds-toggle { display: flex; gap: 0.4rem; }
.ds-toggle button {
  flex: 1; min-height: 38px; padding: 0.4rem; border-radius: 6px; border: 1px solid rgba(255,255,255,0.3);
  background: transparent; color: #e8e8e8; cursor: pointer; font-size: 0.62rem; letter-spacing: 0.12em;
  text-transform: uppercase; transition: all 0.2s;
}
.ds-toggle button:hover { border-color: rgba(255,255,255,0.55); }
.ds-toggle button.active { background: #f2f2f2; color: #000; border-color: #fff; }
.ds-mood { opacity: 0.5; font-size: 0.62rem; text-transform: uppercase; letter-spacing: 0.15em; }
.ds-studio-open {
  min-height: 32px; border-radius: 6px; border: 1px solid rgba(255,255,255,0.28); background: transparent;
  color: #e8e8e8; cursor: pointer; font-size: 0.62rem; letter-spacing: 0.14em; text-transform: uppercase;
  transition: all 0.2s;
}
.ds-studio-open:hover { background: #f2f2f2; color: #000; border-color: #fff; }
.ds-studio-open.active { background: #f2f2f2; color: #000; border-color: #fff; }
/* MIDI-learn badges: hidden until learn mode, then a small chip at the start of each control row. */
.ds-learn {
  display: none; min-width: 22px; height: 18px; padding: 0 0.25rem; border-radius: 4px;
  border: 1px solid rgba(255,255,255,0.4); background: transparent; color: #cfcfcf;
  font-size: 0.55rem; font-family: inherit; cursor: pointer; line-height: 1; flex: 0 0 auto;
}
.ds-panel.midi-learn .ds-learn { display: inline-flex; align-items: center; justify-content: center; }
.ds-learn.bound { border-color: #8fa6c0; color: #cfe0f2; }
.ds-learn.learning { background: #f2c14e; color: #000; border-color: #f2c14e; }
/* Live drive meter: tiny gain-reduction readout next to the Drive slider. */
.ds-meter {
  flex: 0 0 auto; min-width: 48px; text-align: right; font-size: 0.55rem; font-variant-numeric: tabular-nums;
  letter-spacing: 0.04em; opacity: 0.85; color: #8fbf8f;
}
.ds-meter.warn { color: #e8c46a; }
.ds-meter.hot { color: #e08a6a; font-weight: 600; }
.ds-preset-nav { display: flex; gap: 0.3rem; align-items: stretch; }
.ds-arrow {
  width: 2.2rem; min-height: 38px; border-radius: 6px; border: 1px solid rgba(255,255,255,0.3);
  background: transparent; color: #e8e8e8; cursor: pointer; font-size: 0.7rem; transition: all 0.2s; flex: 0 0 auto;
}
.ds-arrow:hover { background: #f2f2f2; color: #000; border-color: #fff; }
.ds-select {
  /* min-width:0 lets the select shrink below the (long) preset name so the ▶ arrow isn't pushed
     out of the panel on mobile; without it, flex min-width:auto overflows the container. */
  flex: 1; min-width: 0; min-height: 38px; padding: 0.4rem 0.5rem; border-radius: 6px; border: 1px solid rgba(255,255,255,0.3);
  background: #000; color: #fff; cursor: pointer; font-size: 0.66rem; letter-spacing: 0.08em;
  text-transform: uppercase; -webkit-appearance: none; appearance: none; text-align: center;
}
.ds-select:hover { border-color: rgba(255,255,255,0.6); }
.ds-select option { background: #000; color: #fff; }
/* On touch devices, 16px form controls stop iOS Safari from zooming the page on focus;
   bump the small header icon buttons to a comfortable tap target too. */
@media (pointer: coarse) {
  .ds-select { font-size: 16px; }
  .ds-min { min-width: 38px; min-height: 38px; }
}
.ds-tune { border: 1px solid rgba(255,255,255,0.18); border-radius: 6px; padding: 0 0.5rem; margin-top: 0.2rem; }
.ds-tune > summary {
  cursor: pointer; list-style: none; padding: 0.45rem 0.1rem; font-size: 0.6rem;
  letter-spacing: 0.18em; text-transform: uppercase; opacity: 0.6;
}
.ds-tune > summary::-webkit-details-marker { display: none; }
.ds-tune[open] > summary { opacity: 0.9; }
.ds-tune .ds-row label { font-size: 0.6rem; }
.ds-tune .ds-tuneval { opacity: 0.95; font-variant-numeric: tabular-nums; margin-left: 0.3rem; }
.ds-tune .ds-tunehint { font-size: 0.55rem; opacity: 0.4; padding: 0.2rem 0 0.45rem; line-height: 1.4; }
.ds-tune.inactive { opacity: 0.45; }
.ds-tune.inactive > summary { cursor: default; }
.ds-info-modal {
  position: fixed; inset: 0; z-index: 20; display: none; align-items: center; justify-content: center;
  background: rgba(0,0,0,0.72); -webkit-backdrop-filter: blur(4px); backdrop-filter: blur(4px); padding: 1.2rem;
}
.ds-info-modal.open { display: flex; }
.ds-info-card {
  max-width: 460px; max-height: 86vh; display: flex; flex-direction: column; overflow: hidden;
  background: #0b0b0b; border: 1px solid rgba(255,255,255,0.25); border-radius: 12px;
  color: #e8e8e8; font-family: system-ui, sans-serif; font-size: 0.8rem; line-height: 1.5; letter-spacing: 0.01em;
}
.ds-info-card h2 { font-weight: 300; letter-spacing: 0.22em; text-transform: uppercase; font-size: 0.95rem; margin: 0; }
.ds-info-card h3 { font-size: 0.7rem; letter-spacing: 0.14em; text-transform: uppercase; opacity: 0.55; margin: 1rem 0 0.25rem; }
.ds-info-card p { margin: 0.2rem 0; opacity: 0.85; }
.ds-info-card b { font-weight: 600; opacity: 1; }
.ds-info-card .ds-attr { font-size: 0.72rem; opacity: 0.7; margin: 0.35rem 0; }
.ds-info-card a { color: #cfe3ff; }
/* Fixed header + independently scrolling body (avoids sticky/backdrop-filter paint quirks). */
.ds-info-top {
  display: flex; align-items: center; gap: 0.5rem; flex: 0 0 auto;
  padding: 1.1rem 1.3rem 0.6rem; background: #0b0b0b; border-bottom: 1px solid rgba(255,255,255,0.12);
}
.ds-info-top h2 { flex: 1; }
.ds-info-body { overflow-y: auto; padding: 0.6rem 1.3rem 1.4rem; }
.ds-info-card ul { margin: 0.3rem 0; padding-left: 1.05rem; }
.ds-info-card li { margin: 0.3rem 0; opacity: 0.85; }
.ds-info-card svg.ds-flow { width: 100%; max-width: 300px; height: auto; display: block; margin: 0.6rem auto 0.3rem; }
`;

let styleInjected = false;
function injectStyle(): void {
  if (styleInjected) return;
  const el = document.createElement('style');
  el.textContent = STYLE;
  document.head.appendChild(el);
  styleInjected = true;
}

export function createEnterOverlay(onEnter: () => Promise<void>): HTMLElement {
  injectStyle();
  const overlay = document.createElement('div');
  overlay.className = 'ds-overlay';
  overlay.innerHTML = `
    <h1>Diatom</h1>
    <p>Microscopic glass shells, each one a resonant body. Strike them, or let the current
       play them for you as it drifts through the deep.</p>
    <button class="ds-enter">Dive in</button>
    <div class="ds-hint">headphones recommended &middot; best on desktop</div>
  `;
  const btn = overlay.querySelector('.ds-enter') as HTMLButtonElement;
  btn.addEventListener('click', async () => {
    btn.disabled = true;
    btn.textContent = 'Surfacing…';
    try {
      await onEnter();
      overlay.style.opacity = '0';
      setTimeout(() => overlay.remove(), 1200);
    } catch (err) {
      btn.disabled = false;
      btn.textContent = 'Dive in';
      console.error('[diatom-synth] boot failed', err);
      const hint = overlay.querySelector('.ds-hint') as HTMLElement;
      hint.textContent = 'audio failed to start — tap again';
    }
  });
  return overlay;
}

export interface PanelCallbacks {
  onDial: (name: 'calmActive' | 'darkBright' | 'pureRough' | 'roaming', value: number) => void;
  onMode: (mode: 'generative' | 'playable') => void;
  onPreset: (id: string) => void;
  onVolume: (value: number) => void;
  onOpenStudio: () => void;
  onBank: (id: string) => void;
  onAmbient: (value: number) => void;
  onGravity: (on: boolean) => void;
  onDrive: (value: number) => void;
  onTune: (param: 'targetRms' | 'grainMs' | 'cloudGain' | 'cloudMs', value: number) => void;
  /** MIDI button tapped (enable on first tap, then toggle learn mode). */
  onMidiToggle: () => void;
  /** Arm a control to bind to the next CC moved. */
  onMidiLearn: (id: string) => void;
}

export interface PanelHandle {
  el: HTMLElement;
  setMood: (mood: string) => void;
  setPreset: (id: string) => void;
  setDials: (vals: Partial<Record<'calmActive' | 'darkBright' | 'pureRough' | 'roaming', number>>) => void;
  setUserScenes: (names: string[]) => void;
  setVolume: (v: number) => void;
  setBank: (id: string) => void;
  setAmbient: (v: number) => void;
  /** Reveal/hide the Gravity button (only offered where device motion is available). */
  setGravityAvailable: (show: boolean) => void;
  /** Reflect the current gravity on/off state on the button. */
  setGravityOn: (on: boolean) => void;
  setDrive: (v: number) => void;
  /** Live drive meter readout (compressor gain reduction + clip state). */
  setMeter: (m: { peak: number; rms: number; reductionDb: number; clipping: boolean }) => void;
  setTuning: (vals: Partial<Record<'targetRms' | 'grainMs' | 'cloudGain' | 'cloudMs', number>>) => void;
  /** Enable/disable the Grain tuning section (disabled + dimmed when it would have no audible effect). */
  setGrainTuningActive: (active: boolean) => void;
  /** Reveal the MIDI button (only when Web MIDI is supported). */
  setMidiAvailable: (show: boolean) => void;
  /** Enter/exit MIDI-learn mode (reveals per-control learn badges). */
  setMidiLearnMode: (on: boolean) => void;
  /** Show a control's bound CC (or null to clear) on its badge. */
  setMidiBinding: (id: string, cc: number | null) => void;
  /** Highlight the control currently armed for learning (or null). */
  setMidiLearning: (id: string | null) => void;
  /** Apply a normalized 0..1 MIDI value to a control (drives its slider + effect). */
  applyMidi: (id: string, value01: number) => void;
}

export function createGardenPanel(
  cb: PanelCallbacks,
  presets: ReadonlyArray<{ id: string; name: string }>,
  banks: ReadonlyArray<{ id: string; name: string; attribution?: string | undefined }>,
): PanelHandle {
  injectStyle();
  const panel = document.createElement('div');
  panel.className = 'ds-panel';

  // MIDI-learn: each learnable slider gets a badge (shown only in learn mode) that arms it.
  const learnInputs = new Map<string, { input: HTMLInputElement; badge: HTMLButtonElement }>();
  const midiBindings = new Map<string, number>(); // id -> bound CC (for badge display)
  const registerLearnable = (id: string, row: HTMLElement, input: HTMLInputElement): void => {
    const badge = document.createElement('button');
    badge.className = 'ds-learn';
    badge.type = 'button';
    badge.textContent = '○';
    badge.title = `MIDI learn: ${id}`;
    badge.setAttribute('aria-label', `MIDI learn ${id}`);
    badge.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      cb.onMidiLearn(id);
    });
    row.insertBefore(badge, row.firstChild);
    learnInputs.set(id, { input, badge });
  };

  const dials: Array<[PanelCallbacks['onDial'] extends (n: infer N, v: number) => void ? N : never, string]> = [
    ['calmActive', 'Calm — Active'],
    ['darkBright', 'Dark — Bright'],
    ['pureRough', 'Pure — Rough'],
    ['roaming', 'Roaming'],
  ];

  // Preset selector: ◀ [dropdown] ▶
  const presetWrap = document.createElement('div');
  presetWrap.className = 'ds-preset-nav';
  const prevBtn = document.createElement('button');
  prevBtn.className = 'ds-arrow';
  prevBtn.textContent = '◀';
  prevBtn.setAttribute('aria-label', 'previous scene');
  const select = document.createElement('select');
  select.className = 'ds-select';
  select.setAttribute('aria-label', 'Scene preset');
  for (const p of presets) {
    const opt = document.createElement('option');
    opt.value = p.id;
    opt.textContent = p.name;
    select.appendChild(opt);
  }
  const nextBtn = document.createElement('button');
  nextBtn.className = 'ds-arrow';
  nextBtn.textContent = '▶';
  nextBtn.setAttribute('aria-label', 'next scene');
  presetWrap.append(prevBtn, select, nextBtn);

  const step = (dir: number) => {
    const n = presets.length;
    select.selectedIndex = (select.selectedIndex + dir + n) % n;
    cb.onPreset(select.value);
  };
  prevBtn.addEventListener('click', () => step(-1));
  nextBtn.addEventListener('click', () => step(1));
  select.addEventListener('change', () => cb.onPreset(select.value));

  const moodEl = document.createElement('div');
  moodEl.className = 'ds-mood';
  moodEl.textContent = 'mood: —';

  const toggle = document.createElement('div');
  toggle.className = 'ds-toggle';
  const genBtn = document.createElement('button');
  genBtn.textContent = 'Generative';
  genBtn.className = 'active';
  const playBtn = document.createElement('button');
  playBtn.textContent = 'Playable';
  toggle.append(genBtn, playBtn);
  genBtn.addEventListener('click', () => {
    genBtn.classList.add('active');
    playBtn.classList.remove('active');
    cb.onMode('generative');
  });
  playBtn.addEventListener('click', () => {
    playBtn.classList.add('active');
    genBtn.classList.remove('active');
    cb.onMode('playable');
  });

  const studioBtn = document.createElement('button');
  studioBtn.className = 'ds-studio-open';
  studioBtn.textContent = 'Diatoms ▸';
  studioBtn.addEventListener('click', () => cb.onOpenStudio());

  // header with minimize, + a restore square shown only when minimized
  const head = document.createElement('div');
  head.className = 'ds-panel-head';
  const titleEl = document.createElement('span');
  titleEl.className = 'ds-panel-title';
  titleEl.textContent = 'Diatom';
  const infoBtn = document.createElement('button');
  infoBtn.className = 'ds-min';
  infoBtn.textContent = 'ⓘ';
  infoBtn.title = 'about the controls';
  infoBtn.setAttribute('aria-label', 'about the controls');
  const minBtn = document.createElement('button');
  minBtn.className = 'ds-min';
  minBtn.textContent = '–';
  minBtn.title = 'minimize controls';
  minBtn.setAttribute('aria-label', 'minimize controls');
  head.append(titleEl, infoBtn, minBtn);

  const infoModal = buildInfoModal(banks);
  document.body.appendChild(infoModal.el);
  infoBtn.addEventListener('click', () => infoModal.open());
  const restore = document.createElement('button');
  restore.className = 'ds-restore';
  restore.textContent = '▣';
  restore.title = 'show controls';
  restore.setAttribute('aria-label', 'show controls');
  minBtn.addEventListener('click', () => panel.classList.add('minimized'));
  restore.addEventListener('click', () => panel.classList.remove('minimized'));

  // Excitation source: which grain "sound bank" rings the diatoms (or None = noise strikes).
  const bankRow = document.createElement('div');
  bankRow.className = 'ds-row';
  const bankLbl = document.createElement('label');
  bankLbl.textContent = 'Excite';
  const bankSelect = document.createElement('select');
  bankSelect.className = 'ds-select';
  bankSelect.id = 'ds-bank';
  bankLbl.htmlFor = bankSelect.id;
  bankSelect.setAttribute('aria-label', 'Excitation sound bank');
  for (const b of banks) {
    const opt = document.createElement('option');
    opt.value = b.id;
    opt.textContent = b.name;
    bankSelect.appendChild(opt);
  }
  bankSelect.addEventListener('change', () => cb.onBank(bankSelect.value));
  bankRow.append(bankLbl, bankSelect);

  // Ambient layer: the active bank's recording as a looping bed (0 = off).
  const ambientRow = document.createElement('div');
  ambientRow.className = 'ds-row';
  const ambientLbl = document.createElement('label');
  ambientLbl.textContent = 'Ambient';
  const ambientInput = document.createElement('input');
  ambientInput.type = 'range';
  ambientInput.min = '0';
  ambientInput.max = '1';
  ambientInput.step = '0.01';
  ambientInput.value = '0';
  ambientInput.id = 'ds-ambient';
  ambientLbl.htmlFor = ambientInput.id;
  ambientInput.setAttribute('aria-label', 'Ambient sound-bank layer level');
  ambientInput.addEventListener('input', () => cb.onAmbient(parseFloat(ambientInput.value)));
  ambientRow.append(ambientLbl, ambientInput);

  // Drive meter: shows output LOUDNESS (RMS dBFS) — what reads as "heavy" — peak-held so it catches
  // peaks then eases down, with a clip flag. (Loudness, not gain reduction: a sustained drone is
  // loud/heavy without peak-limiting, so GR alone missed it.)
  let meterHeldLevel = -60;
  let meterClipHold = 0;

  // Gravity (device tilt): opt-in, hidden until the app detects motion is available.
  let gravityOn = false;
  const gravBtn = document.createElement('button');
  gravBtn.className = 'ds-studio-open';
  gravBtn.textContent = 'Gravity';
  gravBtn.style.display = 'none';
  gravBtn.setAttribute('aria-pressed', 'false');
  gravBtn.title = 'tilt your phone to pour the diatoms; shake to scatter';
  gravBtn.addEventListener('click', () => cb.onGravity(!gravityOn));

  // MIDI: enable Web MIDI + toggle learn mode (hidden unless Web MIDI is supported).
  const midiBtn = document.createElement('button');
  midiBtn.className = 'ds-studio-open';
  midiBtn.textContent = 'MIDI';
  midiBtn.style.display = 'none';
  midiBtn.setAttribute('aria-pressed', 'false');
  midiBtn.title = 'map a hardware MIDI knob to a control';
  midiBtn.addEventListener('click', () => cb.onMidiToggle());

  panel.append(head, restore, presetWrap, toggle, studioBtn, moodEl, bankRow, ambientRow, gravBtn, midiBtn);
  registerLearnable('ambient', ambientRow, ambientInput);

  // Master volume
  const volRow = document.createElement('div');
  volRow.className = 'ds-row';
  const volLbl = document.createElement('label');
  volLbl.textContent = 'Volume';
  const volInput = document.createElement('input');
  volInput.type = 'range';
  volInput.min = '0';
  volInput.max = '1.4';
  volInput.step = '0.01';
  volInput.value = '1';
  volInput.id = 'ds-volume';
  volLbl.htmlFor = 'ds-volume';
  volInput.setAttribute('aria-label', 'Volume');
  volInput.addEventListener('input', () => cb.onVolume(parseFloat(volInput.value)));
  volRow.append(volLbl, volInput);
  panel.appendChild(volRow);
  registerLearnable('volume', volRow, volInput);

  // Drive: master overdrive amount, with a live gain-reduction meter for auditing.
  const driveRow = document.createElement('div');
  driveRow.className = 'ds-row';
  const driveLbl = document.createElement('label');
  driveLbl.textContent = 'Drive';
  const driveInput = document.createElement('input');
  driveInput.type = 'range';
  driveInput.min = '0.3';
  driveInput.max = '1.3';
  driveInput.step = '0.01';
  driveInput.value = '1';
  driveInput.id = 'ds-drive';
  driveLbl.htmlFor = driveInput.id;
  driveInput.setAttribute('aria-label', 'Master drive (overdrive amount)');
  driveInput.addEventListener('input', () => cb.onDrive(parseFloat(driveInput.value)));
  const driveMeter = document.createElement('span');
  driveMeter.className = 'ds-meter';
  driveMeter.title = 'live output loudness (dBFS) — green = light, amber = full, red = heavy/clipping';
  driveMeter.textContent = '0 dB';
  driveRow.append(driveLbl, driveInput, driveMeter);
  panel.appendChild(driveRow);
  registerLearnable('drive', driveRow, driveInput);

  const dialInputs = new Map<string, HTMLInputElement>();
  for (const [name, label] of dials) {
    const row = document.createElement('div');
    row.className = 'ds-row';
    const lbl = document.createElement('label');
    lbl.textContent = label;
    const input = document.createElement('input');
    input.type = 'range';
    input.min = '0';
    input.max = '1';
    input.step = '0.01';
    input.value = '0.5';
    input.id = `ds-dial-${name}`;
    lbl.htmlFor = input.id;
    input.setAttribute('aria-label', label.replace(/\s*—\s*/g, ' to '));
    input.addEventListener('input', () => cb.onDial(name, parseFloat(input.value)));
    dialInputs.set(name, input);
    row.append(lbl, input);
    panel.appendChild(row);
    registerLearnable(name, row, input);
  }

  // Grain-engine tuning sliders (collapsible). Live — only audible when a Sound bank is active.
  const tune = document.createElement('details');
  tune.className = 'ds-tune';
  const sum = document.createElement('summary');
  sum.textContent = 'Grain tuning ▸';
  tune.appendChild(sum);
  type TuneParam = PanelCallbacks['onTune'] extends (p: infer P, v: number) => void ? P : never;
  const tuners: Array<[TuneParam, string, number, number, number, number, number]> = [
    ['targetRms', 'Grain level', 0.005, 0.25, 0.005, 0.05, 3],
    ['grainMs', 'Grain length ms', 10, 200, 5, 55, 0],
    ['cloudGain', 'Cloud level', 0, 2, 0.05, 0.6, 2],
    ['cloudMs', 'Cloud grain ms', 40, 400, 10, 120, 0],
  ];
  const tuneInputs = new Map<TuneParam, { input: HTMLInputElement; val: HTMLElement; dec: number }>();
  for (const [param, label, min, max, stepv, def, dec] of tuners) {
    const row = document.createElement('div');
    row.className = 'ds-row';
    const lbl = document.createElement('label');
    lbl.textContent = label;
    const val = document.createElement('span');
    val.className = 'ds-tuneval';
    val.textContent = def.toFixed(dec);
    lbl.appendChild(val);
    const input = document.createElement('input');
    input.type = 'range';
    input.min = String(min);
    input.max = String(max);
    input.step = String(stepv);
    input.value = String(def);
    input.setAttribute('aria-label', label);
    input.addEventListener('input', () => {
      const v = parseFloat(input.value);
      val.textContent = v.toFixed(dec);
      cb.onTune(param, v);
    });
    row.append(lbl, input);
    tune.appendChild(row);
    tuneInputs.set(param, { input, val, dec });
    registerLearnable(param, row, input);
  }
  const hint = document.createElement('div');
  hint.className = 'ds-tunehint';
  hint.textContent = 'Shapes the grain/ocean-bank sound. Pick a Sound bank + Plankton Bloom / Tidewrack Drift to hear it.';
  tune.appendChild(hint);
  panel.appendChild(tune);

  return {
    el: panel,
    setMood: (mood: string) => {
      moodEl.textContent = `mood: ${mood}`;
    },
    setPreset: (id: string) => {
      select.value = id;
    },
    setDials: (vals) => {
      for (const [name, v] of Object.entries(vals)) {
        const input = dialInputs.get(name);
        if (input && typeof v === 'number') input.value = String(v);
      }
    },
    setUserScenes: (names) => {
      select.querySelector('optgroup[label="User"]')?.remove();
      if (names.length === 0) return;
      const grp = document.createElement('optgroup');
      grp.label = 'User';
      for (const n of names) {
        const o = document.createElement('option');
        o.value = `user:${n}`;
        o.textContent = n;
        grp.appendChild(o);
      }
      select.appendChild(grp);
    },
    setVolume: (v) => {
      volInput.value = String(v);
    },
    setBank: (id) => {
      bankSelect.value = id;
    },
    setAmbient: (v) => {
      ambientInput.value = String(v);
    },
    setGravityAvailable: (show) => {
      gravBtn.style.display = show ? '' : 'none';
    },
    setGravityOn: (on) => {
      gravityOn = on;
      gravBtn.classList.toggle('active', on);
      gravBtn.setAttribute('aria-pressed', String(on));
      gravBtn.textContent = on ? 'Gravity: On' : 'Gravity';
    },
    setDrive: (v) => {
      driveInput.value = String(v);
    },
    setMeter: (m) => {
      // output loudness in dBFS; peak-hold (snap up to louder, ease down) so it's readable.
      const lvl = 20 * Math.log10(Math.max(m.rms, 1e-3));
      if (lvl > meterHeldLevel) meterHeldLevel = lvl;
      else meterHeldLevel += (lvl - meterHeldLevel) * 0.07;
      if (m.clipping) meterClipHold = 1;
      else meterClipHold = Math.max(0, meterClipHold - 0.05);
      const db = Math.round(meterHeldLevel);
      const clip = meterClipHold > 0.05;
      driveMeter.textContent = clip ? `${db} dB ⚠` : `${db} dB`;
      driveMeter.classList.toggle('hot', clip || db >= -10); // loud/heavy or clipping
      driveMeter.classList.toggle('warn', !clip && db >= -16 && db < -10);
    },
    setMidiAvailable: (show) => {
      midiBtn.style.display = show ? '' : 'none';
    },
    setGrainTuningActive: (active) => {
      tune.classList.toggle('inactive', !active);
      for (const { input } of tuneInputs.values()) input.disabled = !active;
      if (!active) tune.open = false; // collapse when it can't do anything
      hint.textContent = active
        ? 'Shapes the grain/ocean-bank sound — live.'
        : 'No effect here. Pick a grain scene (Plankton Bloom / Tidewrack Drift) + a Sound bank.';
    },
    setMidiLearnMode: (on) => {
      panel.classList.toggle('midi-learn', on);
      midiBtn.classList.toggle('active', on);
      midiBtn.setAttribute('aria-pressed', String(on));
      midiBtn.textContent = on ? 'MIDI Learn: On' : 'MIDI';
    },
    setMidiBinding: (id, cc) => {
      const e = learnInputs.get(id);
      if (cc === null) midiBindings.delete(id);
      else midiBindings.set(id, cc);
      if (!e) return;
      e.badge.classList.remove('learning');
      e.badge.classList.toggle('bound', cc !== null);
      e.badge.textContent = cc === null ? '○' : String(cc);
    },
    setMidiLearning: (id) => {
      for (const [k, e] of learnInputs) {
        e.badge.classList.remove('learning');
        const cc = midiBindings.get(k);
        e.badge.classList.toggle('bound', cc !== undefined);
        e.badge.textContent = cc === undefined ? '○' : String(cc);
      }
      if (id) {
        const e = learnInputs.get(id);
        if (e) {
          e.badge.classList.add('learning');
          e.badge.textContent = '…';
        }
      }
    },
    applyMidi: (id, value01) => {
      const e = learnInputs.get(id);
      if (!e) return;
      const min = parseFloat(e.input.min);
      const max = parseFloat(e.input.max);
      const v = min + (max - min) * Math.max(0, Math.min(1, value01));
      e.input.value = String(v);
      e.input.dispatchEvent(new Event('input')); // reuse the control's existing handler
    },
    setTuning: (vals) => {
      for (const [param, v] of Object.entries(vals)) {
        const t = tuneInputs.get(param as TuneParam);
        if (t && typeof v === 'number') {
          t.input.value = String(v);
          t.val.textContent = v.toFixed(t.dec);
        }
      }
    },
  };
}

// Monochrome vertical signal-flow diagram for the About modal.
const FLOW_SVG = `
<svg class="ds-flow" viewBox="0 0 300 338" role="img"
     aria-label="Signal flow: a diatom's microscope geometry sets the resonator modes; an excitation rings that modal bank; the result is shaped and sent through the master bus to your ears.">
  <defs>
    <marker id="ds-arr" markerWidth="9" markerHeight="9" refX="4.5" refY="4" orient="auto">
      <path d="M1,1 L6,4 L1,7" fill="none" stroke="#cfcfcf" stroke-width="1.2"/>
    </marker>
  </defs>
  <g font-family="system-ui, sans-serif">
    ${[
      ['Diatom', 'SEM microscope image', 6],
      ['Excitation', 'strike · bow · ocean grain', 70],
      ['Modal resonator bank', 'tuned filters · one per mode', 134],
      ['Shape', 'saturate · stereo pan', 198],
      ['Master bus', 'reverb · compressor · delay', 262],
    ]
      .map(
        ([t, s, y]) => `
      <rect x="24" y="${y}" width="252" height="40" rx="8" fill="rgba(255,255,255,0.05)" stroke="rgba(255,255,255,0.32)"/>
      <text x="150" y="${(y as number) + 17}" text-anchor="middle" fill="#f0f0f0" font-size="12.5" font-weight="600">${t}</text>
      <text x="150" y="${(y as number) + 31}" text-anchor="middle" fill="#9b9b9b" font-size="9.5">${s}</text>`,
      )
      .join('')}
    <g stroke="#cfcfcf" stroke-width="1.3">
      <line x1="150" y1="46" x2="150" y2="69" marker-end="url(#ds-arr)"/>
      <line x1="150" y1="110" x2="150" y2="133" marker-end="url(#ds-arr)"/>
      <line x1="150" y1="174" x2="150" y2="197" marker-end="url(#ds-arr)"/>
      <line x1="150" y1="238" x2="150" y2="261" marker-end="url(#ds-arr)"/>
      <line x1="150" y1="302" x2="150" y2="320" marker-end="url(#ds-arr)"/>
    </g>
    <text x="158" y="61" fill="#8fa6c0" font-size="8.5" font-style="italic">geometry → modes</text>
    <text x="158" y="125" fill="#8fa6c0" font-size="8.5" font-style="italic">rings the body</text>
    <text x="150" y="334" text-anchor="middle" fill="#9b9b9b" font-size="9.5">↓ your ears</text>
  </g>
</svg>`;

/** The "ⓘ" help modal: what every control does + sample attribution. */
function buildInfoModal(
  banks: ReadonlyArray<{ id: string; name: string; attribution?: string | undefined }>,
): { el: HTMLElement; open: () => void } {
  const modal = document.createElement('div');
  modal.className = 'ds-info-modal';
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');
  modal.setAttribute('aria-labelledby', 'ds-info-title');
  const card = document.createElement('div');
  card.className = 'ds-info-card';

  const attributions = banks
    .filter((b) => b.attribution)
    .map((b) => `<p class="ds-attr"><b>${b.name}</b> — ${b.attribution}</p>`)
    .join('');

  card.innerHTML = `
    <div class="ds-info-top">
      <h2 id="ds-info-title">Diatom</h2>
      <button class="ds-min ds-info-close" aria-label="close">✕</button>
    </div>
    <div class="ds-info-body">
    <p>Microscopic glass shells, each a tuned resonant body. Strike them, throw them, stir the water,
       or let the current play them — they drift and ring when they collide. (See <b>Playing it</b>
       below for every gesture.)</p>

    <h3>How it works</h3>
    <p>Every diatom is <b>physically modelled</b>, not sampled. A bank of tuned resonators (one per
       vibration mode) is built from the shell's real microscope geometry — its shape class, symmetry
       and pore density set each mode's <b>pitch, decay and loudness</b>. So a shell's voice is
       literally derived from its image. An <b>excitation</b> — a noise pluck (strike), filtered noise
       (bow), or a grain of an ocean recording — feeds that bank and makes it ring. The excitation
       supplies the energy; the shell's geometry shapes the tone.</p>
    ${FLOW_SVG}

    <p>Nothing here is sampled. The resonators are built from real diatom microscopy, so each shell
       sounds like its own shape. Choose an ocean bank and the recording isn't a backing track — it's
       chopped into grains that strike the resonators directly, so the sea plays the glass. The shells
       drift on a current and ring when they touch, which makes stirring the field an instrument in
       itself. And a single stream of numbers drives the sound and the picture at once, so they
       breathe together.</p>

    <h3>Scene preset (◀ ▶)</h3>
    <p>Pick a sonic world — each sets the pitch scale, timbre, rhythm, reverb and motion. Presets you
       save appear under <b>User</b> at the bottom of the list.</p>

    <h3>Playing it</h3>
    <p><b>Click / tap</b> a diatom to strike it. <b>Drag and release</b> to throw it across the tank —
       it rings the shells it bumps. <b>Move the cursor</b> through the field (no click needed) to stir
       the water like a current, setting nearby diatoms drifting and colliding. On a phone, <b>swipe</b>
       to stir, turn on <b>Gravity</b> to tilt-pour the field, and <b>shake</b> to scatter it.</p>

    <h3>Generative · Playable</h3>
    <p><b>Generative</b>: it plays itself — the current drifts the field and shells ring as they meet.
       <b>Playable</b>: the current stops and the field falls still, silent until you play it. Your
       taps, throws and stirs always sound in either mode.</p>

    <h3>Why some diatoms are darker</h3>
    <p>The field has <b>depth</b>. Diatoms further away are drawn smaller and dimmer, drift more
       slowly, and sound quieter; nearer ones are larger, brighter and louder. The dim shells aren't
       disabled or muted — they're just deeper in the water.</p>

    <h3>Diatoms</h3>
    <p>Opens the per-diatom mixer: <b>M</b> mute, <b>S</b> solo, plus volume and excitation
       <b>Source</b> (Auto / Strike / Bow) for each shell. Save the whole setup as a preset there.</p>

    <h3>Excite (sound banks)</h3>
    <p>Chooses the recording the diatoms are played from. <b>None</b> = a clean noise pluck (the
       original sound). A bank — <b>Surf (synth)</b> or a real recording — can be used three ways:
       <b>grains</b> that ring the shells (<b>Plankton Bloom</b>, and the bowed “grain cloud” of
       <b>Tidewrack Drift</b>), a <b>direct sampler</b> that plays the recording itself on each hit
       (<b>Sampled Tide</b>), and an <b>Ambient</b> bed.</p>

    <h3>Ambient</h3>
    <p>Loops the selected bank as a continuous background wash, under everything else. 0 = off.</p>

    <h3>Volume &amp; dials</h3>
    <p><b>Calm–Active</b>: density + drift speed. <b>Dark–Bright</b>: register/brightness.
       <b>Pure–Rough</b>: harmonicity. <b>Roaming</b>: how far diatoms wander.</p>

    <h3>Drive</h3>
    <p>How hard the mix hits the saturator/limiter — the “overdriven” character. Each scene has a
       tuned starting point; lower it for a cleaner, more dynamic sound, raise it for grit. The
       number beside it is the live output loudness in dB (green = light, amber = full, red =
       heavy/clipping) — watch it to compare how hard each scene pushes.</p>

    <h3>Grain tuning</h3>
    <p>Shapes the sound-bank grains. It's dimmed unless the current scene actually rings the shells with
       grains (a grain scene + a Sound bank). <b>Grain level</b>: strike loudness. <b>Grain length</b>:
       short = clicky, long = more recording texture. <b>Cloud level</b>: drone loudness. <b>Cloud
       grain</b>: drone smoothness.</p>

    <h3>MIDI</h3>
    <p>On Chromium browsers, tap <b>MIDI</b> to enable a hardware controller, then in learn mode click
       a slider's <b>○</b> badge and move a knob to map it. Bindings are remembered between visits.</p>

    <h3>Sample attribution</h3>
    ${attributions || '<p class="ds-attr">No recordings loaded.</p>'}
    <p class="ds-attr"><b>Surf (synth)</b> — generated in code, no recording.</p>

    <h3>Credits</h3>
    <p>Made by Alex in Vancouver, BC, Canada · <a href="mailto:info@2bz.ca">info@2bz.ca</a></p>
    <p class="ds-attr">Provided free of charge and “as is”, without warranty of any kind. You use it
       at your own risk; the author accepts no liability for any loss or damage arising from its use.</p>
    </div>
  `;

  modal.appendChild(card);
  const closeBtn = card.querySelector('.ds-info-close') as HTMLButtonElement;
  let lastFocus: HTMLElement | null = null;

  const onKeydown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') {
      close();
      return;
    }
    if (e.key !== 'Tab') return;
    // Trap focus within the card.
    const focusables = card.querySelectorAll<HTMLElement>(
      'button, [href], input, select, [tabindex]:not([tabindex="-1"])',
    );
    if (focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  function close(): void {
    modal.classList.remove('open');
    document.removeEventListener('keydown', onKeydown, true);
    lastFocus?.focus(); // restore focus to the trigger (the ⓘ button)
  }
  function open(): void {
    lastFocus = document.activeElement as HTMLElement | null;
    modal.classList.add('open');
    document.addEventListener('keydown', onKeydown, true);
    closeBtn.focus(); // move focus into the dialog
  }

  closeBtn.addEventListener('click', close);
  modal.addEventListener('click', (e) => {
    if (e.target === modal) close(); // click the backdrop to dismiss
  });

  return { el: modal, open };
}
